// src/pages/Dashboard/sections/network-services/networkApiServices.js
import { getValidSignature, clearSignatureCache } from '../../../../services/signatureService';

/**
 * Fetch network data including node downlines
 * @param {Object} wallet - Wallet information with connection status
 * @param {Object} selectedNode - The selected node
 * @returns {Object} - Object containing teamStatsData and networkData
 */
export const fetchNetworkData = async (wallet, selectedNode, includeFees = true, feePeriod = '30d') => {
  if (!selectedNode || !wallet.connected) {
    throw new Error('Wallet not connected or no node selected');
  }
  
  try {
    try {
      // Get a valid signature (may use cache)
      const { signature, message } = await getValidSignature(
        wallet.address,
        null, // No blockchain network ID needed
        wallet.type || 'metamask',
        false // Don't force refresh initially
      );
      
      // First get team statistics for the selected node
      const teamStatsResponse = await fetch('https://api.aeronyx.network/api/nodes/team-stats/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: wallet.address,
          signature: signature,
          message: message,
          wallet_type: wallet.type || 'metamask',
          include_fees: includeFees,  // New parameter
          fee_period: feePeriod,      // New parameter
          node_id: selectedNode.id    // Add node_id parameter
        }),
      });
      
      if (!teamStatsResponse.ok) {
        throw new Error(`API error: ${teamStatsResponse.status} ${teamStatsResponse.statusText}`);
      }
      
      const teamStatsResult = await teamStatsResponse.json();
      
      if (!teamStatsResult.success) {
        // Check if it's an authentication error
        if (teamStatsResult.message && (
          teamStatsResult.message.toLowerCase().includes('signature') || 
          teamStatsResult.message.toLowerCase().includes('auth') ||
          teamStatsResult.message.toLowerCase().includes('token'))
        ) {
          // Clear signature cache and retry
          clearSignatureCache();
          throw new Error('Signature validation failed, retrying...');
        }
        throw new Error(teamStatsResult.message || 'Failed to fetch team statistics');
      }
      
      // Now get node downline data
      const networkResponse = await fetch('https://api.aeronyx.network/api/nodes/node-downline/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: wallet.address,
          signature: signature,
          message: message,
          wallet_type: wallet.type || 'metamask',
          include_indirect: true,
          page: 1,
          page_size: 100, // Get a large number of nodes
          node_id: selectedNode.id  // Add node_id parameter
        }),
      });
      
      if (!networkResponse.ok) {
        throw new Error(`API error: ${networkResponse.status} ${networkResponse.statusText}`);
      }
      
      const networkResult = await networkResponse.json();
      
      if (!networkResult.success) {
        throw new Error(networkResult.message || 'Failed to fetch network data');
      }
      
      return {
        teamStatsData: teamStatsResult.data,
        networkData: networkResult.data
      };
      
    } catch (signError) {
      console.warn('First attempt error in fetchNetworkData:', signError);
      
      // If first attempt fails, try again with a forced new signature
      if (signError.message && signError.message.includes('retrying')) {
        try {
          // Get a fresh signature
          const { signature, message } = await getValidSignature(
            wallet.address,
            null, // No blockchain network ID needed
            wallet.type || 'metamask',
            true // Force refresh signature
          );
          
          // Retry team stats API call with new parameters
          const teamStatsResponse = await fetch('https://api.aeronyx.network/api/nodes/team-stats/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              wallet_address: wallet.address,
              signature: signature,
              message: message,
              wallet_type: wallet.type || 'metamask',
              include_fees: includeFees,
              fee_period: feePeriod,
              include_orders: true,  // Request order data
              order_status: 'completed',  // Filter for completed orders
              node_id: selectedNode.id  // Add node_id parameter
            }),
          });
          
          if (!teamStatsResponse.ok) {
            throw new Error(`API error: ${teamStatsResponse.status} ${teamStatsResponse.statusText}`);
          }
          
          const teamStatsResult = await teamStatsResponse.json();
          
          if (!teamStatsResult.success) {
            throw new Error(teamStatsResult.message || 'Failed to fetch team statistics');
          }
          
          // Retry node downline API call
          const networkResponse = await fetch('https://api.aeronyx.network/api/nodes/node-downline/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              wallet_address: wallet.address,
              signature: signature,
              message: message,
              wallet_type: wallet.type || 'metamask',
              include_indirect: true,
              page: 1,
              page_size: 100,
              node_id: selectedNode.id  // Add node_id parameter
            }),
          });
          
          if (!networkResponse.ok) {
            throw new Error(`API error: ${networkResponse.status} ${networkResponse.statusText}`);
          }
          
          const networkResult = await networkResponse.json();
          
          if (!networkResult.success) {
            throw new Error(networkResult.message || 'Failed to fetch network data');
          }
          
          return {
            teamStatsData: teamStatsResult.data,
            networkData: networkResult.data
          };
          
        } catch (retryError) {
          console.error('Retry failed in fetchNetworkData:', retryError);
          throw retryError;
        }
      } else {
        // If it's not a signature error, rethrow
        throw signError;
      }
    }
  } catch (err) {
    console.error('Error in fetchNetworkData service:', err);
    throw err;
  }
};

/**
 * Fetch points data for the selected node
 * @param {Object} wallet - Wallet information
 * @param {Object} selectedNode - The selected node
 * @param {string} pointsPeriod - The period to fetch points for
 * @returns {Array} - Array of points data
 */
export const fetchPointsData = async (wallet, selectedNode, pointsPeriod) => {
  if (!selectedNode || !wallet.connected) {
    throw new Error('Wallet not connected or no node selected');
  }
  
  try {
    try {
      // Get a valid signature with potential caching
      const { signature, message } = await getValidSignature(
        wallet.address,
        null, // No blockchain network ID needed
        wallet.type || 'metamask',
        false // Don't force refresh initially
      );
      
      // Fetch points data
      const response = await fetch('https://api.aeronyx.network/api/nodes/team-points/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: wallet.address,
          signature: signature,
          message: message,
          wallet_type: wallet.type || 'metamask',
          node_id: selectedNode.id,
          period: pointsPeriod
        }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        // Check if it's an authentication error
        if (result.message && (
          result.message.toLowerCase().includes('signature') || 
          result.message.toLowerCase().includes('auth') ||
          result.message.toLowerCase().includes('token'))
        ) {
          // Clear signature cache and retry
          clearSignatureCache();
          throw new Error('Signature validation failed, retrying...');
        }
        throw new Error(result.message || 'Failed to fetch points data');
      }
      
      // Return points data
      return result.data?.points_history || [];
      
    } catch (signError) {
      console.warn('First attempt error in fetchPointsData:', signError);
      
      // If first attempt fails, try again with a forced new signature
      if (signError.message && signError.message.includes('retrying')) {
        try {
          // Get a fresh signature
          const { signature, message } = await getValidSignature(
            wallet.address,
            null, // No blockchain network ID needed
            wallet.type || 'metamask',
            true // Force refresh signature
          );
          
          // Retry the API call with the new signature
          const response = await fetch('https://api.aeronyx.network/api/nodes/team-points/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              wallet_address: wallet.address,
              signature: signature,
              message: message,
              wallet_type: wallet.type || 'metamask',
              node_id: selectedNode.id,
              period: pointsPeriod
            }),
          });
          
          if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
          }
          
          const result = await response.json();
          
          if (!result.success) {
            throw new Error(result.message || 'Failed to fetch points data');
          }
          
          // Return points data
          return result.data?.points_history || [];
          
        } catch (retryError) {
          console.error('Retry failed in fetchPointsData:', retryError);
          throw retryError;
        }
      } else {
        // If it's not a signature error, rethrow
        throw signError;
      }
    }
  } catch (err) {
    console.error('Error in fetchPointsData service:', err);
    throw err;
  }
};

/**
 * Fetch network stability fees for the selected node
 * @param {Object} wallet - Wallet information
 * @param {string} feePeriod - The period to fetch fees for
 * @returns {Array} - Array of fees data
 */
export const fetchNetworkStabilityFees = async (wallet, selectedNode, feePeriod = '30d') => {
  if (!wallet.connected) {
    throw new Error('Wallet not connected');
  }
  
  try {
    try {
      // Get a valid signature with potential caching
      const { signature, message } = await getValidSignature(
        wallet.address,
        null, // No blockchain network ID needed
        wallet.type || 'metamask',
        false // Don't force refresh initially
      );
      
      // Build request body
      const requestBody = {
        wallet_address: wallet.address,
        signature: signature,
        message: message,
        wallet_type: wallet.type || 'metamask',
        period: feePeriod,
        node_id: selectedNode.id  // Add node_id parameter
      };
      
      // Fetch fees data
      const response = await fetch('https://api.aeronyx.network/api/points/network-stability-fees/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        // Check if it's an authentication error
        if (result.message && (
          result.message.toLowerCase().includes('signature') || 
          result.message.toLowerCase().includes('auth') ||
          result.message.toLowerCase().includes('token'))
        ) {
          // Clear signature cache and retry
          clearSignatureCache();
          throw new Error('Signature validation failed, retrying...');
        }
        throw new Error(result.message || 'Failed to fetch network stability fees');
      }
      
      // Process the response to enhance with additional information
      const feesData = result.data;
      
      // Ensure fees array exists and has expected properties
      if (feesData && feesData.fees) {
        // Add is_self_referral flag if not present
        feesData.fees = feesData.fees.map(fee => {
          const isSelfReferral = fee.source_wallet && 
                                 fee.recipient_wallet &&
                                 fee.source_wallet.toLowerCase() === fee.recipient_wallet.toLowerCase();
          
          return {
            ...fee,
            is_self_referral: fee.is_self_referral || isSelfReferral
          };
        });
        
        // Calculate totals for self-referrals vs. external if not already provided
        if (feesData.summary) {
          if (feesData.summary.self_referral_amount === undefined) {
            const selfReferralFees = feesData.fees.filter(fee => fee.is_self_referral);
            const selfReferralAmount = selfReferralFees.reduce((sum, fee) => sum + fee.amount, 0);
            const totalAmount = feesData.summary.total_fees_received || 0;
            
            feesData.summary.self_referral_amount = selfReferralAmount;
            feesData.summary.self_referral_count = selfReferralFees.length;
            feesData.summary.external_fees_amount = totalAmount - selfReferralAmount;
          }
        }
      }
      
      return feesData;
      
    } catch (signError) {
      console.warn('First attempt error in fetchNetworkStabilityFees:', signError);
      
      // If first attempt fails, try again with a forced new signature
      if (signError.message && signError.message.includes('retrying')) {
        try {
          // Get a fresh signature
          const { signature, message } = await getValidSignature(
            wallet.address,
            null, // No blockchain network ID needed
            wallet.type || 'metamask',
            true // Force refresh signature
          );
          
          // Retry the API call with the new signature
          const response = await fetch('https://api.aeronyx.network/api/points/network-stability-fees/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              wallet_address: wallet.address,
              signature: signature,
              message: message,
              wallet_type: wallet.type || 'metamask',
              period: feePeriod,
              node_id: selectedNode.id  // Add node_id parameter
            }),
          });
          
          if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
          }
          
          const result = await response.json();
          
          if (!result.success) {
            throw new Error(result.message || 'Failed to fetch network stability fees');
          }
          
          // Process data as in the first attempt
          const feesData = result.data;
          
          if (feesData && feesData.fees) {
            feesData.fees = feesData.fees.map(fee => {
              const isSelfReferral = fee.source_wallet && 
                                    fee.recipient_wallet &&
                                    fee.source_wallet.toLowerCase() === fee.recipient_wallet.toLowerCase();
              
              return {
                ...fee,
                is_self_referral: fee.is_self_referral || isSelfReferral
              };
            });
            
            if (feesData.summary) {
              if (feesData.summary.self_referral_amount === undefined) {
                const selfReferralFees = feesData.fees.filter(fee => fee.is_self_referral);
                const selfReferralAmount = selfReferralFees.reduce((sum, fee) => sum + fee.amount, 0);
                const totalAmount = feesData.summary.total_fees_received || 0;
                
                feesData.summary.self_referral_amount = selfReferralAmount;
                feesData.summary.self_referral_count = selfReferralFees.length;
                feesData.summary.external_fees_amount = totalAmount - selfReferralAmount;
              }
            }
          }
          
          return feesData;
          
        } catch (retryError) {
          console.error('Retry failed in fetchNetworkStabilityFees:', retryError);
          throw retryError;
        }
      } else {
        // If it's not a signature error, rethrow
        throw signError;
      }
    }
  } catch (err) {
    console.error('Error in fetchNetworkStabilityFees service:', err);
    throw err;
  }
};

/**
 * Search for nodes in the network
 * @param {Object} wallet - Wallet information
 * @param {string} searchQuery - The search query
 * @returns {Array} - Array of search results
 */
export const searchNodes = async (wallet, searchQuery) => {
  if (!wallet.connected || !searchQuery.trim()) {
    throw new Error('Wallet not connected or empty search query');
  }
  
  try {
    try {
      // Get a valid signature with potential caching
      const { signature, message } = await getValidSignature(
        wallet.address,
        null, // No blockchain network ID needed
        wallet.type || 'metamask',
        false // Don't force refresh initially
      );
      
      // Fetch search results
      const response = await fetch('https://api.aeronyx.network/api/nodes/search/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: wallet.address,
          signature: signature,
          message: message,
          wallet_type: wallet.type || 'metamask',
          query: searchQuery,
          page: 1,
          page_size: 10
        }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        // Check if it's an authentication error
        if (result.message && (
          result.message.toLowerCase().includes('signature') || 
          result.message.toLowerCase().includes('auth') ||
          result.message.toLowerCase().includes('token'))
        ) {
          // Clear signature cache and retry
          clearSignatureCache();
          throw new Error('Signature validation failed, retrying...');
        }
        throw new Error(result.message || 'Failed to search nodes');
      }
      
      // Return search results
      return result.data?.nodes || [];
      
    } catch (signError) {
      console.warn('First attempt error in searchNodes:', signError);
      
      // If first attempt fails, try again with a forced new signature
      if (signError.message && signError.message.includes('retrying')) {
        try {
          // Get a fresh signature
          const { signature, message } = await getValidSignature(
            wallet.address,
            null, // No blockchain network ID needed
            wallet.type || 'metamask',
            true // Force refresh signature
          );
          
          // Retry the API call with the new signature
          const response = await fetch('https://api.aeronyx.network/api/nodes/search/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              wallet_address: wallet.address,
              signature: signature,
              message: message,
              wallet_type: wallet.type || 'metamask',
              query: searchQuery,
              page: 1,
              page_size: 10
            }),
          });
          
          if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
          }
          
          const result = await response.json();
          
          if (!result.success) {
            throw new Error(result.message || 'Failed to search nodes');
          }
          
          // Return search results
          return result.data?.nodes || [];
          
        } catch (retryError) {
          console.error('Retry failed in searchNodes:', retryError);
          throw retryError;
        }
      } else {
        // If it's not a signature error, rethrow
        throw signError;
      }
    }
  } catch (err) {
    console.error('Error in searchNodes service:', err);
    throw err;
  }
};
